import { createNEQRequest, getNEQResponse, setNBILine } from '../../integration/nbi.api';
import useLocalStorage from '../../hooks/useLocalStorage';
import { prequalPreOrderAnalytics } from '../../../util/gtm';
import { setCookie } from '../../hooks/useCookie';

const localStore = useLocalStorage();

/**
 * @name NEQResponse - Function for getting NEQ Results
 * @param {*} graphql - From useApolloClient
 * @param {string} eirCode - Eircode provided by user on prequal form
 * @returns {object} - NEQ response object
 */

export const NEQResponse = async (
  graphql,
  prequalId,
  setNEQDetails,
  setLoading,
  errorBBModal,
  errorModalOccurence,
  errorModalMaxOccurence,
  neqCounter,
  setNeqCounter,
  dataRetrievalModal,
  setErrorModalOccurence,
  setOnLoadError,
  openAPIFailureModal,
  getNEQCounter,
  setIsPrequaled,
  setShowLoader,
  setprequalCTALoaderText
) => {
  try {
    setLoading(true);
    const { data, status, error } = await getNEQResponse({
      graphql,
      variables: {
        prequalId: JSON.parse(prequalId),
      },
    });
    /* eslint-disable no-param-reassign */
    getNEQCounter += 1;
    if (!error && getNEQCounter <= 15) {
      if (getNEQCounter >= 3) {
        setprequalCTALoaderText('Almost there');
      }
      if (status === 'PENDING') {
        setLoading(true);
        setTimeout(() => {
          NEQResponse(
            graphql,
            prequalId,
            setNEQDetails,
            setLoading,
            errorBBModal,
            errorModalOccurence,
            errorModalMaxOccurence,
            neqCounter,
            setNeqCounter,
            dataRetrievalModal,
            setErrorModalOccurence,
            setOnLoadError,
            openAPIFailureModal,
            getNEQCounter,
            setIsPrequaled,
            setShowLoader,
            setprequalCTALoaderText
          );
        }, 2000);
      } else {
        setNEQDetails(data);
        setLoading(false);
        setIsPrequaled(true);
        setShowLoader(false);
      }
    } else {
      setCookie('ION', 'API_FAILED', 'getNEQResult');
      if (getNEQCounter > 15) {
        setOnLoadError(true);
        dataRetrievalModal.enable();
        setErrorModalOccurence(2);
      } else {
        setOnLoadError(true);
        setErrorModalOccurence(errorModalOccurence + 1);
        errorBBModal.enable();
        if (error[0]?.extensions?.code === 'nbi-enquiry-rejected') {
          openAPIFailureModal.enable();
        } else {
          dataRetrievalModal.enable();
        }
      }
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * @name setPremisesLineForUG - Function for getting NEQ Results
 * @param {*} graphql - From useApolloClient
 * @param {string} eirCode - Eircode provided by user on prequal form
 * @returns {object} - NEQ response object
 */

export const setPremisesLineForUG = async (
  graphql,
  lineDetails,
  errorBBModal,
  errorModalOccurence,
  dataRetrievalModal,
  setErrorModalOccurence,
  setOnLoadError,
  openAPIFailureModal
) => {
  try {
    const { data, status, error } = await setNBILine({
      graphql,
      variables: {
        prequalId: parseInt(
          JSON.parse(localStore.getLocalStorage('PREQUAL_RESULT') || '{}')?.prequalId,
          10
        ),
        lineDetails,
      },
    });
    if (!error) {
      if (status === 'PENDING') {
        setTimeout(() => {
          setPremisesLineForUG(
            graphql,
            lineDetails,
            errorBBModal,
            errorModalOccurence,
            dataRetrievalModal,
            setErrorModalOccurence,
            setOnLoadError,
            openAPIFailureModal
          );
        }, 2000);
      } else {
        return data;
      }
    } else {
      setOnLoadError(true);
      setErrorModalOccurence(errorModalOccurence + 1);
      errorBBModal.enable();
      if (error[0]?.extensions?.code === 'nbi-enquiry-rejected') {
        openAPIFailureModal.enable();
      } else {
        dataRetrievalModal.enable();
      }
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * @name triggerNEQ - Function for creating an NEQ Order
 * @param {*} graphql - From useApolloClient
 * @param {string} eirCode - Eircode provided by user on prequal form
 * @returns {string} - NEQ request response
 */

export const triggerNEQ = async (
  graphql,
  setNEQDetails,
  setLoading,
  errorBBModal,
  errorModalOccurence,
  errorModalMaxOccurence,
  neqCounter,
  setNeqCounter,
  dataRetrievalModal,
  setErrorModalOccurence,
  setOnLoadError,
  openAPIFailureModal,
  setIsPrequaled,
  setShowLoader,
  setprequalCTALoaderText
) => {
  const prequalId = JSON.parse(localStore.getLocalStorage('PREQUAL_RESULT') || '{}')?.prequalId;
  setprequalCTALoaderText('Hang on a moment');
  setLoading(true);
  try {
    const { data, error } = await createNEQRequest({
      graphql,
      variables: {
        eircode: JSON.parse(localStore.getLocalStorage('PREQUAL_RESULT') || '{}')?.eircode,
        prequalId: JSON.parse(prequalId),
      },
    });
    setLoading(true);
    if (!error) {
      if (data === 'success') {
        let getNEQCounter = 0;
        setLoading(true);
        getNEQCounter = 1;
        NEQResponse(
          graphql,
          prequalId,
          setNEQDetails,
          setLoading,
          errorBBModal,
          errorModalOccurence,
          errorModalMaxOccurence,
          neqCounter,
          setNeqCounter,
          dataRetrievalModal,
          setErrorModalOccurence,
          setOnLoadError,
          openAPIFailureModal,
          getNEQCounter,
          setIsPrequaled,
          setShowLoader,
          setprequalCTALoaderText
        );
      }
    } else {
      setOnLoadError(true);
      setErrorModalOccurence(errorModalOccurence + 1);
      errorBBModal.enable();
      dataRetrievalModal.enable();
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * @name NEQValidationForPremises - Function to validate premises line with NEQ properties
 * @param {object} premisesNEQDetails - NEQ response object obtained as part of setNBILine
 * @param {Boolean} - Enable login modal for an existing NBI
 * @param {Boolean} - Loader for displaying tariffs
 * @param {Boolean} - Enable NBI error modals
 */

export const NEQValidationForPremises = (
  premisesNEQDetails,
  setEnableModal,
  setLoading,
  enableNBIPrevention,
  setNEQDetails
) => {
  setLoading(true);
  const premisesLineDetails = premisesNEQDetails?.lines[0];
  delete premisesNEQDetails?.lines;
  const premisesNEQLineDetails = { ...premisesNEQDetails, ...premisesLineDetails };
  setNEQDetails(premisesNEQLineDetails);
  if (premisesLineDetails?.lineId && premisesLineDetails?.activeLines === 1) {
    setEnableModal(true);
  } else if (
    premisesLineDetails?.pendingInstallation ||
    premisesLineDetails?.reserved ||
    !premisesNEQDetails?.interventionArea ||
    premisesNEQDetails?.surveyRequired ||
    premisesNEQDetails?.activeLines >= 1
  ) {
    enableNBIPrevention.enable();
    setLoading(false);
  }
  setLoading(false);
};

/**
 * @name getLineDetails - Function to build lineDetails Object
 * @param {object} NEQDetails - NEQ response object
 * @param {object} premisesLine - premises line details
 * @param {Boolean} - Enable login modal for an existing NBI
 * @param {Boolean} - Loader for displaying tariffs
 * @param {Boolean} - Enable NBI error modals
 */

const getLineDetails = (NEQDetails, premisesLine) => ({
  interventionArea: NEQDetails?.interventionArea,
  surveyRequired: NEQDetails?.surveyRequired,
  reserved: premisesLine?.reserved,
  pendingInstallation: premisesLine?.pendingInstallation,
  activeLines: premisesLine?.activeLines,
  preOrderFlag: NEQDetails?.preOrderFlag,
  readyForServiceDate: NEQDetails?.readyForServiceAt,
  wayLeaveRequired: NEQDetails?.wayLeaveRequired,
  status: premisesLine?.status,
  ontActive: premisesLine?.ontActive,
  lineId: premisesLine?.lineId,
  addressId: premisesLine?.addressId,
  tag: NEQDetails?.tag,
});

/**
 * @name preventNBICheck - Function to display prevent NBI modals
 * @param {object} NEQDetails - NEQ response object
 * @param {Boolean} - Enable login modal for an existing NBI
 * @param {Boolean} - Loader for displaying tariffs
 * @param {Boolean} - Enable NBI error modals
 */

export const preventNBICheck = async (
  Graphql,
  NEQDetails,
  setEnableModal,
  setLoading,
  enableNBIPrevention,
  setNEQDetails,
  setNBIMultiline,
  errorBBModal,
  errorModalOccurence,
  dataRetrievalModal,
  setErrorModalOccurence,
  setOnLoadError,
  openAPIFailureModal,
  path
) => {
  if (NEQDetails?.multiplePremises) {
    const premisesLine = NEQDetails?.lines?.filter(line => line.name === '{Premises}')[0];
    if (premisesLine) {
      const lineDetails = getLineDetails(NEQDetails, premisesLine);
      const premisesNEQDetails = await setPremisesLineForUG(
        Graphql,
        lineDetails,
        errorBBModal,
        errorModalOccurence,
        dataRetrievalModal,
        setErrorModalOccurence,
        setOnLoadError,
        openAPIFailureModal
      );
      if (premisesNEQDetails) {
        setNBIMultiline(true);
        NEQValidationForPremises(
          premisesNEQDetails,
          setEnableModal,
          setLoading,
          enableNBIPrevention,
          setNEQDetails
        );
      }
    } else {
      // This block is to handle, no {premises} line scenario
      enableNBIPrevention.enable();
      setLoading(false);
    }
  } else if (NEQDetails?.lineId && NEQDetails?.activeLines === 1) {
    setEnableModal(true);
  } else if (
    NEQDetails?.pendingInstallation ||
    NEQDetails?.reserved ||
    !NEQDetails?.interventionArea ||
    NEQDetails?.surveyRequired ||
    NEQDetails?.activeLines >= 1
  ) {
    // setLoading(true);
    enableNBIPrevention.enable();
    setLoading(false);
  } else if (NEQDetails?.preOrderFlag) {
    prequalPreOrderAnalytics('NBI', path);
  }
};
